import { useMemo } from 'react'
import { format } from 'date-fns'

import { EnhancedDataTable, type ColumnConfig } from '@/components/tables/enhanced-data-table'
import { Skeleton } from '@/components/ui/skeleton'
import type { TrainingNeedItem } from '../types'

function formatDate(value: string | null): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }
  try {
    return format(parsed, 'PPpp')
  } catch {
    return '—'
  }
}

export interface TrainingNeedsTableProps {
  records: TrainingNeedItem[]
  totalCount: number
  pageSize: number
  onPageChange: (pageIndex: number, pageSize: number) => void
  onPageSizeChange: (pageSize: number) => void
  loading?: boolean
  emptyMessage?: string
  stateKey?: string | number
  searchPlaceholder?: string
}

export function TrainingNeedsTable({
  records,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading = false,
  emptyMessage = 'No training needs have been submitted yet.',
  stateKey,
  searchPlaceholder = 'Search training needs…',
}: TrainingNeedsTableProps) {
  const columns: ColumnConfig<TrainingNeedItem>[] = useMemo(
    () => [
      {
        key: 'topic',
        header: 'Training topic',
        accessor: (row) => row.topic,
        sortable: true,
      },
      {
        key: 'organisationName',
        header: 'Organisation',
        accessor: (row) => row.organisationName,
        sortable: true,
      },
      {
        key: 'createdAt',
        header: 'Submitted on',
        accessor: (row) => row.createdAt,
        cell: (value) => formatDate(typeof value === 'string' ? value : null),
        sortable: true,
      },
    ],
    [],
  )

  if (loading && records.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={`training-need-skeleton-${index}`} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  return (
    <EnhancedDataTable
      key={stateKey}
      data={records}
      columns={columns}
      enablePagination
      enableSorting
      enableGlobalFilter
      enableExport={false}
      enableColumnFilters={false}
      searchPlaceholder={searchPlaceholder}
      loading={loading}
      emptyStateMessage={emptyMessage}
      paginationCallbacks={{
        manualPagination: true,
        totalCount,
        onPageChange,
        onPageSizeChange,
      }}
      pageSize={pageSize}
    />
  )
}
